import React from "react";
import { useTranslation } from "react-i18next";
import { Building2, Wrench } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import type { AccessRequestStatus, CompanyEntry } from "../../types/smartId";

type Role = "building" | "provider";

type Props = {
  entry: CompanyEntry;
  /** Whitelisted at login. Untrusted sessions register / join through an access request (ticket 6). */
  trusted: boolean;
  onLoginBuilding: (buildingId: string) => void;
  onLoginProvider: (providerId: string) => void;
  onRegisterBuilding: (registryCode: string) => void;
  onRegisterProvider: (registryCode: string) => void;
  onJoinBuilding: (entry: CompanyEntry) => void;
  onJoinProvider: (entry: CompanyEntry) => void;
  onRequestAccess: (entry: CompanyEntry, role: Role) => void;
  disabled?: boolean;
  /** No authoritative representation source configured — see `SelectRolePage`. */
  selfOnboardingUnavailable?: boolean;
};

type Action =
  | {
      kind: "button";
      label: string;
      variant: "default" | "outline";
      onClick: () => void;
      /**
       * No authoritative representation source is configured, so registering or
       * joining cannot be checked (ticket 13). Logging in to an entity you are
       * already a member of is never blocked — membership is a DB row.
       */
      blocked?: boolean;
    }
  | { kind: "badge"; label: string };

const REQUEST_LABEL: Record<AccessRequestStatus, string> = {
  PENDING: "selectRole.request.pending",
  REJECTED: "selectRole.request.rejected",
  APPROVED: "selectRole.request.approved",
};

export function EntityCard({
  entry,
  trusted,
  onLoginBuilding,
  onLoginProvider,
  onRegisterBuilding,
  onRegisterProvider,
  onJoinBuilding,
  onJoinProvider,
  onRequestAccess,
  disabled,
  selfOnboardingUnavailable,
}: Props) {
  const { t } = useTranslation();

  // Register / join for a role: trusted sessions keep the instant onboarding;
  // a guest with a request for that role sees its status, otherwise the same
  // label files the request.
  const blockOnboarding = selfOnboardingUnavailable === true;

  const registerOrJoin = (role: Role, label: string, instant: () => void): Action => {
    if (trusted) {
      return { kind: "button", label, variant: "outline", onClick: instant, blocked: blockOnboarding };
    }
    const wanted = role === "building" ? "BUILDING" : "PROVIDER";
    const request = (entry.accessRequests ?? []).find((r) => r.role === wanted);
    if (request) return { kind: "badge", label: t(REQUEST_LABEL[request.status]) };
    // Filing a request needs no representation source — the operator checks it.
    return { kind: "button", label, variant: "outline", onClick: () => onRequestAccess(entry, role) };
  };

  const buildingAction: Action = entry.buildingLinked
    ? { kind: "button", label: t("selectRole.actions.loginAsBuilding"), variant: "default",
        onClick: () => onLoginBuilding(entry.building!.id) }
    : entry.building
    ? registerOrJoin("building", t("selectRole.actions.joinAsBoardMember"), () => onJoinBuilding(entry))
    // This branch is reachable only for a registry hint — it has no `building`
    // row — and a hint always carries a code (ticket 19). The guard narrows the
    // now-nullable type without an assertion; the row simply offers nothing if
    // the invariant ever breaks.
    : registerOrJoin("building", t("selectRole.actions.registerAsBuilding"), () => entry.registryCode && onRegisterBuilding(entry.registryCode));

  const providerAction: Action = entry.providerLinked
    ? { kind: "button", label: t("selectRole.actions.loginAsProvider"), variant: "default",
        onClick: () => onLoginProvider(entry.provider!.id) }
    : entry.provider
    ? registerOrJoin("provider", t("selectRole.actions.joinAsRep"), () => onJoinProvider(entry))
    // Same reasoning as the building branch above.
    : registerOrJoin("provider", t("selectRole.actions.registerAsProvider"), () => entry.registryCode && onRegisterProvider(entry.registryCode));

  // Once the user is linked to this company in one role, hide the opposite-role
  // action — a housing association doesn't double as a service provider (and
  // vice versa). Unlinked entries still see both actions so a new user can pick
  // either role.
  const unavailableTitle = t("selectRole.selfOnboardingUnavailable");

  const showBuildingAction = entry.buildingLinked || !entry.providerLinked;
  const showProviderAction = entry.providerLinked || !entry.buildingLinked;

  return (
    <div className="flex flex-col gap-3 p-4 rounded-xl border border-border bg-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          {/* A company this person belongs to as a contractor gets the tool, not
              the house (BUG-26). A company that is both keeps the house — the
              building side is the one with an address and apartments behind it;
              a registry hint is nobody's membership yet, so it keeps it too. */}
          {entry.providerLinked && !entry.buildingLinked ? (
            <Wrench className="w-5 h-5 text-primary" data-testid="entity-icon-provider" />
          ) : (
            <Building2 className="w-5 h-5 text-primary" data-testid="entity-icon-building" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-foreground">{entry.name}</div>
          {entry.address && (
            <div className="text-sm text-muted-foreground mt-0.5 truncate">{entry.address}</div>
          )}
          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
            <span>{[entry.legalForm, entry.registryCode].filter(Boolean).join(" · ")}</span>
            {/* A hint says the registry page mentions this company — not that
                you represent it. Ticket 2's marker idiom. */}
            {entry.source === "registry_hint" && (
              <span
                className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 text-xs font-medium"
                title={t("selectRole.hint.tooltip")}
              >
                {t("selectRole.hint.badge")}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        {showBuildingAction && renderAction(buildingAction, disabled, unavailableTitle)}
        {showProviderAction && renderAction(providerAction, disabled, unavailableTitle)}
      </div>
    </div>
  );
}

function renderAction(action: Action, disabled?: boolean, blockedTitle?: string) {
  if (action.kind === "badge") {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Badge variant="secondary">{action.label}</Badge>
      </div>
    );
  }
  return (
    <Button
      variant={action.variant}
      disabled={disabled || action.blocked}
      onClick={action.onClick}
      className="flex-1"
      title={action.blocked ? blockedTitle : undefined}
    >
      {action.label}
    </Button>
  );
}
